import React, {useLayoutEffect, useRef} from "react";
import {connect} from "react-redux";
import {styled} from "@reside/ui";

import {AppState, select} from "../../store";
import type {WorkflowStatus} from "../../services/WorkflowsService";

import {
  DashboardBoardScrollArea,
  DashboardBoardScrollShell,
} from "./DashboardBoardScroll";
import {DashboardBoardScrollMinimap} from "./DashboardBoardScrollMinimap";
import {DashboardGridColumn} from "./DashboardGridColumn";
import {DASHBOARD_COLUMN_DATA} from "./DashboardTable";
import {DASHBOARD_GRID_LAYOUT_SYNC_EVENT} from "./dashboardGridLayoutSync";

/** Board scrolls horizontally when the viewport is narrower than four min-width tracks plus gaps. */
export const DASHBOARD_TILE_COLUMN_MIN_WIDTH_PX = 290;
const DASHBOARD_TILE_COLUMN_GAP_PX = 16;

/** Extra space so the Load More button stays inside the column bottom border. */
const COLUMN_BOTTOM_CLEARANCE_PX = 8;

const measureIntrinsicColumnHeight = (column: HTMLElement) => {
  const header = column.children[0] as HTMLElement | undefined;
  const cardsPadding = column.children[1] as HTMLElement | undefined;
  const fromChildren =
    (header?.offsetHeight ?? 0) + (cardsPadding?.scrollHeight ?? 0);
  return (
    Math.max(column.scrollHeight, fromChildren) + COLUMN_BOTTOM_CLEARANCE_PX
  );
};

const Renderer = ({
  starredOnly,
  workflowType,
  query,
  facilityIds,
}: {
  starredOnly?: boolean;
  workflowType?: string | null;
  /** Dashboard search term, matched against resident name and workflow code. */
  query?: string | null;
  facilityIds: ReadonlyArray<string> | null;
}) => {
  /* No query here — each column owns its own, as against the Java service today. The service
     accepts several statuses at once, so the four can be collapsed later without a contract
     change, given a measurement to justify it. */
  const boardScrollRef = useRef<HTMLDivElement | null>(null);
  const boardGridRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const syncBoardGridHeight = () => {
      const scrollArea = boardScrollRef.current;
      const boardGrid = boardGridRef.current;
      if (!scrollArea || !boardGrid) return;

      const columns = Array.from(
        boardGrid.querySelectorAll<HTMLElement>("[data-test-id$='-column']"),
      );

      const tallestIntrinsic = Math.max(
        0,
        ...columns.map(measureIntrinsicColumnHeight),
      );
      const minBoardHeight = Math.max(
        scrollArea.clientHeight,
        tallestIntrinsic,
      );
      const contentExceedsViewport =
        tallestIntrinsic > scrollArea.clientHeight + 2;

      /* Compare content to viewport, not current grid height — after minHeight is
         applied offsetHeight already matches, and a later sync must not clear it. */
      boardGrid.style.minHeight = contentExceedsViewport
        ? `${minBoardHeight}px`
        : "";
    };

    const resizeObserver = new ResizeObserver(syncBoardGridHeight);

    if (boardScrollRef.current) {
      resizeObserver.observe(boardScrollRef.current);
    }

    window.addEventListener(
      DASHBOARD_GRID_LAYOUT_SYNC_EVENT,
      syncBoardGridHeight,
    );
    syncBoardGridHeight();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener(
        DASHBOARD_GRID_LAYOUT_SYNC_EVENT,
        syncBoardGridHeight,
      );
      if (boardGridRef.current) {
        boardGridRef.current.style.minHeight = "";
      }
    };
  }, [starredOnly, workflowType]);

  return (
    <DashboardBoardScrollShell>
      <DashboardBoardScrollArea ref={boardScrollRef}>
        <BoardGrid ref={boardGridRef}>
          {DASHBOARD_COLUMN_DATA.map(({status, loadMoreButtonId}) => {
            return (
              <DashboardGridColumn
                key={status}
                status={status as WorkflowStatus}
                loadMoreButtonId={loadMoreButtonId}
                facilityIds={facilityIds}
                query={query}
                workflowType={workflowType}
                starredOnly={starredOnly}
              />
            );
          })}
        </BoardGrid>
      </DashboardBoardScrollArea>
      <DashboardBoardScrollMinimap
        scrollRef={boardScrollRef}
        columnCount={DASHBOARD_COLUMN_DATA.length}
      />
    </DashboardBoardScrollShell>
  );
};

const BoardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(
    4,
    minmax(${DASHBOARD_TILE_COLUMN_MIN_WIDTH_PX}px, 1fr)
  );
  grid-column-gap: ${DASHBOARD_TILE_COLUMN_GAP_PX}px;
  /* Stretch all columns to the tallest row height so short columns stay visible while scrolling. */
  align-items: stretch;
  /* Fill the scroll shell so empty/loading columns keep height; ColumnBody uses height: 100%. */
  flex: 1 1 auto;
  min-height: 100%;
  /* One implicit row: grow with the board so columns stretch when the grid is taller than content. */
  grid-auto-rows: minmax(min-content, 1fr);
  width: 100%;
  min-width: 100%;
  box-sizing: border-box;
  /* Match dashboard header + layout: same horizontal inset as FullWidthDashboardLayoutContent / --dashboard-h-pad. */
  padding-left: var(--dashboard-h-pad);
  padding-right: var(--dashboard-h-pad);
`;

const mapStateToProps = (state: AppState) => ({
  facilityIds: select.adminSession.activeFacilitiesIds(state),
});

export const DashboardGrid = connect(mapStateToProps)(Renderer);
